import _ from 'lodash';

import { AssetUtils } from 'console-kit';
/**
 * centralised set of useful asset functions
 * most require the passing in of a string asset ref
 * https://docs.google.com/document/d/1egWDo8xHa_V7b9lr28WKVnbzZYbNL6kW40KrEeolX6U
 */

const dimensionsRegexPattern = /^(\d*)(?:x?)(\d*)(?:x?)(\d*)(?:x?)(\d*)$/;

/////////////////////
// Private methods //
/////////////////////

/**
 * remove trailing slash from passed in string
 * @param      {string}     path     the string to update
 * @return     {string}              the updated string with any trailing slash removed
 */
function removeTrailingSlash(path) {
  return path.replace(/\/+$/, '');
}

/**
 * add a leading slash to passed in string
 * @param     {string}     path     the path to udpate
 * @return    {String}              the updated string with the trailing slash added
 */
function addLeadingSlash(path) {
  return /^[^\/]/.test(path) ? '/' + path : path;
}

////////////////////
// Public methods //
////////////////////

/**
 * verify is a path passed in is absolute or relative
 * @param      {string}      path     path string to verify
 * @return     {Boolean}              true if it's an absolute path
 */
function isAbsPath(path) {
  //TODO Replace with common regex when available
  const regCheck = /(^http|^\/\/)/g; // if the string starts with 'http' or '//'
  return regCheck.test(path);
}

/**
 * verify this is a valid asset ref
 * @param      {string}      assetRef     the string to verify
 * @return     {Boolean}                  is it an asset ref?
 */
function isAssetRef(assetRef) {
  const regexPattern =
    /(static|sysui|weburl|ext|asset|bundle|file|ugc|youtube|vimeo|facebook|slidehare|vine|bazaarvoice|flickr|instagram|linkedin|lithium|pinterest|twitter|vk|newscred|mux|pexels):/g;
  return !!assetRef && typeof assetRef === 'string' && regexPattern.test(assetRef);
}

/**
 * return an object describing the integral parts of the asset ref for easy use
 * @param      {string}     assetRef     the asset ref string
 * @return     {object}                  helpful parts of the asset ref broken out
 */
function fileFromAssetRef(assetRef) {
  return this.isAssetRef(assetRef)
    ? {
        prefix: this.prefixFromAssetRef(assetRef),
        service: this.prefixFromAssetRef(assetRef),
        path: this.pathFromAssetRef(assetRef),
        name: this.filenameFromAssetRef(assetRef),
        extension: this.extensionFromAssetRef(assetRef),
        filename: this.filenameFromAssetRef(assetRef),
        type: this.typeFromAssetRef(assetRef),
        dimensions: this.assetDimensions(assetRef),
        settings: AssetUtils.deserialize(assetRef).settings,
        size: 0,
        value: '',
        duration: this.assetDuration(assetRef),
        fps: this.assetFrameRate(assetRef),
      }
    : {};
}

/**
 * return the first element of the asset ref
 * @param      {string}     assetRef     the asset ref
 * @return     {string}                  the first element of the asset ref
 */
function prefixFromAssetRef(assetRef) {
  return this.isAssetRef(assetRef) ? AssetUtils.deserialize(assetRef).service : '';
}

/**
 * return the string portion of the asset ref representing the asset dimensions
 * @param      {string}     assetRef     the asset ref
 * @return     {string}                  the dimensions string. can be ###x###, ### or auto
 */
function assetDimensions(assetRef) {
  const dimensions = this.isAssetRef(assetRef) ? AssetUtils.deserialize(assetRef).dim : '';
  const matches = dimensionsRegexPattern.exec(dimensions);

  if (matches && matches.length === 5) {
    // at this point, your regex matches should contain the dimensions in elements two and three - the first element will be the original string
    return matches.slice(1, 3).join('x');
  } else return dimensions || '';
}

/**
 * return the string portion of the asset ref representing the asset duration
 * @param      {string}     assetRef     the asset ref
 * @return     {string}                  the duration string.
 */
function assetDuration(assetRef) {
  const dimensions = this.isAssetRef(assetRef) ? AssetUtils.deserialize(assetRef).dim : '';
  const matches = dimensionsRegexPattern.exec(dimensions);

  // at this point, your regex matches should contain the duration in the third element
  if (matches && matches.length === 5) {
    return matches[3];
  } else return '';
}

/**
 * return the string portion of the asset ref representing the asset fps
 * @param      {string}     assetRef     the asset ref
 * @return     {string}                  the fps string.
 */
function assetFrameRate(assetRef) {
  const dimensions = this.isAssetRef(assetRef) ? AssetUtils.deserialize(assetRef).dim : '';
  const matches = dimensionsRegexPattern.exec(dimensions);

  // at this point, your regex matches should contain the fps in the fourth element
  if (matches && matches.length === 5) {
    return matches[4];
  } else return '';
}

/**
 * return the full path of the asset
 * @param      {string}     assetRef     the asset ref
 * @return     {string}                  the full path to the asset taking into account it's type
 */
function pathFromAssetRef(assetRef) {
  const // make sure all domain paths have a trailing slash
    _accountAssetResourceURL = removeTrailingSlash(App.globalContext.accountAssetURL), // strip trailing / if present
    _sysResourceURL = removeTrailingSlash(App.globalContext.sysResourceURL), // strip trailing / if present
    _accountUGCResourceURL = removeTrailingSlash(App.globalContext.accountUGCAssetURL), // strip trailing / if present
    _accountUGCDownloadURL = removeTrailingSlash(App.globalContext.accountUGCDownloadURL), // strip trailing / if present
    _staticResourceURL = removeTrailingSlash(App.globalContext.assetURL), // strip trailing / if present
    _consoleAssetUrl = removeTrailingSlash(App.globalContext.consoleAssetURL), // strip trailing / if present
    _muxImageUrl = removeTrailingSlash(App.globalContext.muxImageUrl), // strip trailing / if present
    _muxVideoUrl = removeTrailingSlash(App.globalContext.muxVideoUrl); // strip trailing / if present
  let _assetPath = ''; // use this to store the file path part of the assetRef after we've verified it's an assetRef format string

  // return ''
  if (!assetRef) return '';

  // if this isn't an asset ref format string, assume static for relative paths or just return the absolute path orginally passed in
  if (!this.isAssetRef(assetRef)) {
    if (!isAbsPath(assetRef)) return _staticResourceURL + addLeadingSlash(assetRef);
    // add leading / if not present;
    else return assetRef;
  }

  // now we definitely have an assetRef format string being passed in so let's grab the actual file path portion of that string
  _assetPath = encodeURI(AssetUtils.deserialize(assetRef).location).replace(/&amp;/g, '&');

  const _coreProviders =
    /^(weburl|ext|youtube|vimeo|facebook|slideshare|vine|bazaarvoice|flickr|instagram|linkedin|lithium|pinterest|twitter|vk|newscred|pexels):/;
  if (_coreProviders.test(assetRef)) return _assetPath;
  else if (/^mux:image/.test(assetRef)) return _muxImageUrl + addLeadingSlash(_assetPath);
  else if (/^mux:video/.test(assetRef)) return _muxVideoUrl + addLeadingSlash(_assetPath);
  else if (/^ugc:video/.test(assetRef)) return _accountUGCDownloadURL + addLeadingSlash(_assetPath);
  else if (/^ugc:image/.test(assetRef)) return _accountUGCDownloadURL + addLeadingSlash(_assetPath);
  else if (/^ugc:/.test(assetRef)) return _accountUGCResourceURL + addLeadingSlash(_assetPath);
  else if (/:video:/.test(assetRef)) return _assetPath;
  else if (/^bundle:/.test(assetRef)) return _consoleAssetUrl + addLeadingSlash(_assetPath);
  else if (/^static:/.test(assetRef)) return _staticResourceURL + addLeadingSlash(_assetPath);
  else if (/^sysui:/.test(assetRef)) return _sysResourceURL + addLeadingSlash(_assetPath);
  else return _accountAssetResourceURL + addLeadingSlash(_assetPath);
}

/**
 * return just the filename from the asset ref string
 * @param      {string}     assetRef     the asset ref
 * @return     {string}                  the filename of the asset
 */
function filenameFromAssetRef(assetRef) {
  return this.isAssetRef(assetRef) ? assetRef.substring(assetRef.lastIndexOf('/') + 1) : '';
}

/**
 * return the file extension of the asset in the asset ref string
 * @param      {string}     assetRef     the asset ref
 * @return     {string}                  the file extension of the file in the asset ref
 */
function extensionFromAssetRef(assetRef) {
  const filename = this.filenameFromAssetRef(assetRef);
  return filename ? filename.substring(filename.lastIndexOf('.') + 1) : '';
}

/**
 * return the type of the asset ref
 * @param      {string}     assetRef     the asset ref
 * @return     {string}                  / separated description of the type of the asset
 */
function typeFromAssetRef(assetRef) {
  if (!this.isAssetRef(assetRef)) {
    return '';
  } else if (_.includes(assetRef, ':image:')) {
    return 'image/' + this.extensionFromAssetRef(assetRef);
  } else if (_.includes(assetRef, ':inlinesvg:')) {
    return 'image/' + this.extensionFromAssetRef(assetRef);
  } else if (_.includes(assetRef, ':video:')) {
    return 'movie';
  } else if (_.includes(assetRef, ':file:')) {
    return 'file';
  } else if (_.includes(assetRef, ':gif:')) {
    return 'gif';
  } else {
    return null;
  }
}

function settingsFromAssetRef(assetRef) {
  return this.isAssetRef(assetRef) ? AssetUtils.deserialize(assetRef).settings : '';
}

const services = [
  'static',
  'asset',
  'youtube',
  'instagram',
  'vimeo',
  'twitter',
  'facebook',
  'ext',
  'weburl',
  'background',
  'mux',
];
const types = ['image', 'inlinesvg', 'video', 'file', 'gif'];
const settingsKeys = ['type', 'size', 'attachment', 'repeat', 'color'];

function buildAssetRef(service, type, dimensions, location = '', settings) {
  if (!_.includes(services, service)) {
    throw new TypeError(`Unknown parameter "service": ${service}. Should be one of: ${services.join(', ')}`);
  }

  if (!_.includes(types, type)) {
    throw new TypeError(`Unknown parameter "type": ${type}. Should be one of: ${types.join(', ')}`);
  }

  if (dimensions !== 'auto' && !/\d+x\d+/.test(dimensions)) {
    throw new TypeError(
      `Unknown parameter "dimensions": ${dimensions}. Should be either "auto" or follow this format: <height>x<width>`
    );
  }

  if (
    location &&
    location.indexOf('/') !== 0 &&
    location.indexOf('http://') !== 0 &&
    location.indexOf('https://') !== 0 &&
    service !== 'static'
  ) {
    throw new TypeError(`Unknown parameter "location": ${location}. Should be a http/https protocol or absolute URI`);
  }

  const _location = location.replace(/youtu.be\/(.*)\?(.*)?/, 'youtube.com/watch?v=$1&$2');

  if (settings) {
    _.forEach(settings, (value, key) => {
      if (!_.includes(settingsKeys, key)) {
        throw new TypeError(`Unknown parameter "settings": ${key}. Key should be one of: ${settingsKeys.join(', ')}`);
      }
    });

    const settingsString = encodeURI(
      _.join(
        _.map(settings, (value, key) => `${key}=${value}`),
        '&'
      )
    );

    return [service, type, dimensions, _location, settingsString].join(':');
  } else {
    return [service, type, dimensions, _location].join(':');
  }
}

/**
 * get the value of a param in an assetRef if present
 * @param {string} assetRef an assetRef
 * @param {string} paramName the param name to search for
 * @returns {string} the value of the param you passed in (if present)
 */
function paramFromAssetRef(assetRef, paramName) {
  const regex = new RegExp(`${paramName}=([^&]*)`);
  const matches = regex.exec(assetRef);

  const hasParam = matches !== null && matches.length >= 2;
  return hasParam ? matches[1] : '';
}

/**
 * set the value of a param in an assetRef and return the resulting new assetRef
 * @param {string} assetRef an assetRef
 * @param {string} paramName the param name to search for
 * @param {any} value the value to set the param to
 * @returns {string} the assetRef with the updated param value
 */
function paramToAssetRef(assetRef = '', paramName, value) {
  const regex = new RegExp(`(${paramName}=)[^&]*`);
  return assetRef.replace(regex, `$1${value}`);
}

function isUploadedVideo(asset) {
  const assetType = {
    VIDEO: 'movie',
  };

  const service = {
    MUX: 'mux',
  };
  return asset && asset.type === assetType.VIDEO && asset.prefix === service.MUX;
}

/**
 * builds an asset ref from a destination format type
 * @param      {string} RefData destination
 * @return     {string} assetRef
 */
function assetRefFromDestination(destination) {
  return this.buildAssetRef(
    'weburl',
    'image',
    '130x125',
    `${App.globalContext.assetURL}c/img/experience-workspace/${destination.id.replace(/\./g, '-')}-color.svg`
  );
}

/**
 * builds a generic thumbnail asset ref for the content collections items (used when API returns a apth, containing null)
 * @return     {string} assetRef
 */
function buildGenericThumbnail() {
  return buildAssetRef(
    'weburl',
    'image',
    'auto',
    'https://res.cloudinary.com/engagesciences/image/upload/v1603781669/generic-image-150.png'
  );
}

/**
 * Checks if given asset ref present
 * @param {SeededContent} contentItem
 * @param {string} assetRef
 *
 * @returns {boolean}
 */
function isAssetRefPresentInSeededContentItem(contentItem, assetRef) {
  return contentItem?.media?.[0]?.[assetRef];
}

const assetRefService = {
  isAbsPath,
  isAssetRef,
  isUploadedVideo,
  fileFromAssetRef,
  prefixFromAssetRef,
  assetDimensions,
  pathFromAssetRef,
  filenameFromAssetRef,
  extensionFromAssetRef,
  typeFromAssetRef,
  settingsFromAssetRef,
  buildAssetRef,
  paramFromAssetRef,
  paramToAssetRef,
  assetDuration,
  assetFrameRate,
  assetRefFromDestination,
  buildGenericThumbnail,
  isAssetRefPresentInSeededContentItem,
};
window.assetRefService = assetRefService;

export default assetRefService;
